import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthService } from '@/services/auth';
import { AuthResponse, LoginRequest, RegisterRequest } from '@/types/api';
import { useNavigate } from 'react-router-dom';

// Query keys for React Query
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
};

/**
 * Hook for authentication state and methods
 */
export function useAuth() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Query for current user
  const {
    data: user,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: authKeys.user(),
    queryFn: AuthService.getCurrentUser,
    retry: false,
    enabled: AuthService.isAuthenticated(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (data: LoginRequest) => AuthService.login(data),
    onSuccess: (authResponse: AuthResponse) => {
      // Set user data in cache
      queryClient.setQueryData(authKeys.user(), authResponse.user);
      // Navigate to dashboard
      navigate('/dashboard');
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (data: RegisterRequest) => AuthService.register(data),
    onSuccess: (authResponse: AuthResponse) => {
      // Set user data in cache
      queryClient.setQueryData(authKeys.user(), authResponse.user);
      // Navigate to dashboard
      navigate('/dashboard');
    },
    onError: (error) => {
      console.error('Registration failed:', error);
    },
  });

  // Logout function
  const logout = () => {
    AuthService.logout();
    queryClient.clear(); // Clear all cached data
    navigate('/');
  };

  // Google login function
  const initiateGoogleLogin = () => {
    AuthService.initiateGoogleLogin();
  };

  return {
    // User state
    user,
    isAuthenticated: !!user,
    isLoading,
    isError,
    error,

    // Mutations
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout,
    initiateGoogleLogin,

    // Mutation states
    loginState: {
      isLoading: loginMutation.isPending,
      error: loginMutation.error,
      isError: loginMutation.isError,
    },
    registerState: {
      isLoading: registerMutation.isPending,
      error: registerMutation.error,
      isError: registerMutation.isError,
    },

    // Backward compatibility (if needed by existing components)
    loginError: loginMutation.error,
    registerError: registerMutation.error,
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    googleAuth: initiateGoogleLogin, // Alias for backward compatibility
  };
}